import React, { useState, useEffect, useMemo } from 'react';
import { Search, Sparkles } from 'lucide-react';
import { ContextSearchBar } from '@/components/contextboard/ContextSearchBar';
import { ConstellationLoader } from '@/components/contextboard/ConstellationLoader';
import { ConstellationLayout } from '@/components/contextboard/ConstellationLayout';
import { IntentChips } from '@/components/contextboard/IntentChips';
import { LLMDebugPanel } from '@/components/debug/LLMDebugPanel';

import { useContextSearch } from '@/hooks/use-context-search';
import { useRealTimeNotifications } from '@/hooks/use-real-time-notifications';

const Contextboard = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeIntents, setActiveIntents] = useState<any[]>([]);
  const { search, isSearching, results, error, clearResults } = useContextSearch();
  
  // Memoize notification preferences to prevent re-subscriptions
  const notificationPreferences = useMemo(() => ({
    newContent: true,
    trending: true,
    constellationUpdates: true,
    soundEnabled: false
  }), []);
  
  // Enable real-time notifications for new content
  useRealTimeNotifications(notificationPreferences);

  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    await search(query);
  };

  const handleAddIntent = (intent: any) => {
    setActiveIntents(prev => [...prev, intent]);
    handleSearch(intent.query);
  };

  const handleRemoveIntent = (intentId: string) => {
    setActiveIntents(prev => prev.filter(intent => intent.id !== intentId));
  };

  const handleIntentClick = (intent: any) => {
    if (!activeIntents.find(active => active.id === intent.id)) {
      handleAddIntent(intent);
    } else {
      handleSearch(intent.query);
    }
  };

  const hasResults = results && results.results.length > 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section with Search */}
      <div className="relative">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/5" />
        
        {/* Floating orbs */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-primary/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute top-40 right-20 w-24 h-24 bg-accent/20 rounded-full blur-2xl animate-pulse delay-1000" />
        <div className="absolute bottom-20 left-1/3 w-40 h-40 bg-secondary/10 rounded-full blur-3xl animate-pulse delay-500" />
        
        <div className="relative z-10 container mx-auto px-4 py-16 md:py-24">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            {/* Header */}
            <div className="space-y-4">
              <div className="flex items-center justify-center gap-2 text-primary">
                <Sparkles className="w-6 h-6" />
                <span className="text-sm font-medium uppercase tracking-wider">Context-Aware Discovery</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-foreground via-primary to-accent bg-clip-text text-transparent">
                What do you need right now?
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Tell FOMO your mood, intent, or desire. Watch as content constellations form around your current state.
              </p>
            </div>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <ContextSearchBar 
                onSearch={handleSearch}
                placeholder="I'm not motivated today..."
                isLoading={isSearching}
              />
            </div>

            {/* Intent Chips */}
            <div className="max-w-3xl mx-auto">
              <IntentChips
                activeIntents={activeIntents}
                suggestedIntents={[]}
                onAddIntent={handleAddIntent}
                onRemoveIntent={handleRemoveIntent}
                onIntentClick={handleIntentClick}
              />
            </div>
          </div>
        </div>
      </div>

      {/* LLM Debug Panel */}
      <LLMDebugPanel />

      {/* Content Area */}
      <div className="container mx-auto px-4 pb-16">
        {isSearching && (
          <ConstellationLoader message="Forming constellations..." />
        )}

        {!isSearching && hasResults && results && (
          <ConstellationLayout 
            query={searchQuery} 
            results={results.results.map((item) => {
              // Transform search results for constellation display
              if (item.result_type === 'profile') {
                return {
                  type: 'profile',
                  id: item.profile_id,
                  user: {
                    name: item.author.display_name || item.author.username,
                    username: item.author.username,
                    avatar: item.author.profile_image_url || '/placeholder-avatar.jpg',
                    bio: item.author.bio
                  },
                  profileData: {
                    username: item.author.username,
                    displayName: item.author.display_name,
                    bio: item.author.bio,
                    profileImageUrl: item.author.profile_image_url,
                    isDiscoverable: item.author.is_discoverable
                  },
                  content: item.content.profile_content,
                  score: item.score
                };
              } else if (item.result_type === 'post' && item.author) {
                return {
                  type: item.content.image_url ? 'image_post' : 'post',
                  id: item.post_id,
                  user: {
                    name: item.author.display_name || item.author.username,
                    username: item.author.username,
                    avatar: item.author.profile_image_url || '/placeholder-avatar.jpg'
                  },
                  content: item.content.body || item.content.title,
                  image: item.content.image_url,
                  stats: { 
                    likes: Math.floor(item.score * 100),
                    comments: Math.floor(Math.random() * 20) 
                  },
                  timestamp: new Date(item.content.created_at).toLocaleDateString(),
                  score: item.score
                };
              } else {
                return {
                  type: 'post',
                  id: item.post_id || item.profile_id || Math.random().toString(),
                  user: {
                    name: 'Unknown User',
                    username: 'user',
                    avatar: '/placeholder-avatar.jpg'
                  },
                  content: typeof item.content === 'string' ? item.content : JSON.stringify(item.content),
                  stats: { likes: Math.floor(item.score * 100), comments: 0 },
                  timestamp: 'Recently',
                  score: item.score
                };
              }
            })}
            onContentClick={(contentId) => {
              console.log('Content clicked:', contentId);
            }}
            onContentLike={(contentId) => {
              console.log('Content liked:', contentId);
            }}
            onProfileInteract={(action, profileId) => {
              console.log(`Profile ${action}:`, profileId);
            }}
          />
        )}
        
        {!isSearching && error && (
          <div className="text-center py-16 space-y-4">
            <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto">
              <Search className="w-8 h-8 text-destructive" />
            </div>
            <h3 className="text-lg font-medium text-destructive">
              Search failed
            </h3>
            <p className="text-sm text-muted-foreground">
              {error}
            </p>
            <button 
              onClick={() => clearResults()}
              className="px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg transition-colors"
            >
              Try again
            </button>
          </div>
        )}

        {!isSearching && !hasResults && searchQuery && (
          <div className="text-center py-16 space-y-4">
            <div className="w-16 h-16 bg-muted/30 rounded-full flex items-center justify-center mx-auto">
              <Search className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium text-muted-foreground">
              No results found for "{searchQuery}"
            </h3>
            <p className="text-sm text-muted-foreground/80 max-w-md mx-auto">
              Try searching for something like "freelancers in chicago", "motivation content", or "creative designers"
            </p>
            <div className="flex flex-wrap justify-center gap-2 mt-4">
              {[
                "freelancers in chicago",
                "creative inspiration", 
                "motivational content",
                "local artists"
              ].map((suggestion) => (
                <button
                  key={suggestion}
                  onClick={() => handleSearch(suggestion)}
                  className="px-3 py-1.5 text-xs bg-muted/50 hover:bg-muted/80 rounded-full transition-colors border border-border/50 hover:border-primary/50"
                >
                  Try: {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {!isSearching && !hasResults && !searchQuery && (
          <div className="text-center py-16 space-y-4">
            <Search className="w-16 h-16 text-muted-foreground/30 mx-auto" />
            <h3 className="text-lg font-medium text-muted-foreground">
              Ready to explore
            </h3>
            <p className="text-sm text-muted-foreground/80">
              Type your mood, intent, or question above to discover personalized content constellations
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Contextboard;